import { FrontendBlog } from '../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../core/api/blog-text';
import { BlogPost } from './blog.models';

/**
 * Maps an API blog onto the `BlogPost` shape the blog cards and list pages already render.
 *
 * The blog feature's list pages (trending, featured, archive, search, tag) all feed `BlogCard`,
 * which takes a `BlogPost`. Mapping here instead of in each page keeps those pages thin and means
 * the excerpt / read-time / date rules stay identical wherever a card appears.
 */

const PLACEHOLDER_IMAGE_URL = '/images/blog-placeholder.svg';
const DEFAULT_AUTHOR = 'SavingArena Team';
const DEFAULT_CATEGORY = 'Shopping Tips';

export function stripHtml(value: string): string {
  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function slugifyBlogValue(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Prefixes a relative `/uploads/...` path with the asset host; absolute URLs pass through. */
export function blogAssetUrl(path: string | undefined, assetBaseUrl: string): string {
  if (!path) {
    return '';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base = assetBaseUrl.replace(/\/$/, '');

  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Publish date as `YYYY-MM-DD` — what the cards print and what the archive filters slice. */
export function blogPublishedAt(blog: FrontendBlog): string {
  const raw = blog.publishAt || blog.updatedAt || '';
  if (!raw) {
    return '';
  }

  const parsed = new Date(raw);

  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function excerptFor(blog: FrontendBlog): string {
  const short = stripHtml(blog.shortDescription ?? '');
  if (short) {
    return short;
  }

  const body = stripHtml(blogBodyText(blog));
  if (!body) {
    return blog.name;
  }

  return body.length <= 150 ? body : `${body.slice(0, 150).trimEnd()}...`;
}

function readTimeFor(blog: FrontendBlog): string {
  return `${blogReadMinutes(blog)} min read`;
}

function authorFor(blog: FrontendBlog): string {
  const author = blog['author'];

  if (typeof author === 'string' && author.trim()) {
    return author.trim();
  }

  if (author && typeof author === 'object' && 'name' in author) {
    const name = (author as { name?: unknown }).name;
    if (typeof name === 'string' && name.trim()) {
      return name.trim();
    }
  }

  return DEFAULT_AUTHOR;
}

export function toBlogPost(blog: FrontendBlog, assetBaseUrl: string): BlogPost {
  const categoryName = blog.category?.name || DEFAULT_CATEGORY;
  const author = authorFor(blog);
  // Tags fall back to a slugified name for records saved before tag slugs were served.
  const tags = Array.isArray(blog.tags)
    ? blog.tags.map((tag) => (tag.slug || slugifyBlogValue(tag.name || '')).trim()).filter(Boolean)
    : [];

  return {
    id: blog.id,
    slug: blog.slug,
    title: blog.name,
    excerpt: excerptFor(blog),
    category: categoryName,
    categorySlug: blog.category?.slug || slugifyBlogValue(categoryName),
    author,
    authorSlug: slugifyBlogValue(author),
    publishedAt: blogPublishedAt(blog),
    readTime: readTimeFor(blog),
    imageUrl: blogAssetUrl(blog.thumbnailImagePath, assetBaseUrl) || PLACEHOLDER_IMAGE_URL,
    tags,
    isFeatured: blog.isFeatured === true,
  };
}

export function toBlogPosts(blogs: FrontendBlog[], assetBaseUrl: string): BlogPost[] {
  return blogs.map((blog) => toBlogPost(blog, assetBaseUrl));
}
